import React, { useCallback, useEffect } from "react";
import Moment from 'react-moment';
import { IDocument, IGetDocumentsResponse } from "../../../interfaces/api/IDocuments";
import { Button, Table } from "react-bootstrap";
import { useLazyGetDocumentDownloadQuery, useLazyGetDocumentProgrammationQuery } from "../../../services/api/repositories/DocumentsRepository";
import { useNavigate } from "react-router-dom";

export default function TableDocuments({ data }: { data: IGetDocumentsResponse }) {
    return (
        <div className="table-container">
            <Table className="table list attachs">
                <thead>
                    <tr>
                        <th className="name key">Nom</th>
                        <th className="description">Description</th>
                        <th className="type">Type</th>
                        <th className="date">Client(s) cible</th>
                        <th className="date">Date Ajout</th>
                        <th className="date">Période d'exploitation</th>
                        <th className="status">Annulé</th>
                        <th className="actions">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <TableRows data={data} />
                </tbody>
            </Table>
        </div>
    );
}

function TableRows({ data }: { data: IGetDocumentsResponse }) {
    const rows = data.documents.map((document) => {
        return (
            <Row documentToRender={document} key={document.uuid} />
        );
    });

    return (
        <>
            {rows}
        </>
    );
}

function Row({ documentToRender }: { documentToRender: IDocument }) {
    const navigate = useNavigate();
    const [getDocumentDownload, { data, isSuccess }] = useLazyGetDocumentDownloadQuery();
    const [getDocumentProgrammation, { data: programmationData }] = useLazyGetDocumentProgrammationQuery();

    useEffect(() => {
        if (documentToRender.type === 'legal_cgv') {
            getDocumentProgrammation({ uuid: documentToRender.uuid });
        }
    }, [documentToRender.uuid, documentToRender.type, getDocumentProgrammation]);

    const handleDownload = useCallback(() => {
        getDocumentDownload({ uuid: documentToRender.uuid });
    }, [getDocumentDownload, documentToRender]);

    const handleEdit = useCallback(() => {
        if (documentToRender.type === 'legal_cgv') {
            navigate(`/documents/${documentToRender.uuid}/edit`);
        }
    }, [navigate, documentToRender.uuid, documentToRender.type]);

    const renderPeriod = () => {
        if (documentToRender.type !== 'legal_cgv' || !programmationData || 'message' in programmationData) {
            return 'N/A';
        }

        const { date_validity_start, date_validity_end, is_canceled } = programmationData;

        if (!date_validity_start) {
            return 'N/A';
        }

        const formatDate = (date: string) => {
            return new Date(date).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        };

        if (!date_validity_end) {
            return formatDate(date_validity_start) + ' - N/A';
        }

        return `${formatDate(date_validity_start)} - ${formatDate(date_validity_end)}`;
    };

    const renderCancelStatus = () => {
        if (documentToRender.type !== 'legal_cgv') {
            return 'Non concerné';
        }
        if (!programmationData || 'message' in programmationData) {
            return 'N/A';
        }
        return programmationData.is_canceled ? 'Oui' : 'Non';
    };

    useEffect(() => {
        if (isSuccess && data && data.data && data.data !== "" && data.name && data.name !== "") {

            const base64Data = data.data;
            const binaryString = window.atob(base64Data);
            const arrayBuffer = new ArrayBuffer(binaryString.length);
            const uint8Array = new Uint8Array(arrayBuffer);

            for (let i = 0; i < binaryString.length; i++) {
                uint8Array[i] = binaryString.charCodeAt(i);
            }

            const blob = new Blob([uint8Array], {
                type: 'application/pdf',
            });

            if (blob.type !== 'application/pdf') {
                throw new Error('The file is not a PDF');
            }

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = data.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }
    }, [data, documentToRender, isSuccess]);

    const period = renderPeriod();
    const cancelStatus = renderCancelStatus();

    return (
        <tr key={documentToRender.uuid}>
            <td className="name key">{documentToRender.name}</td>
            <td className="file">{documentToRender.description}</td>
            <td className="type">{documentToRender.type}</td>
            <td className="clientTypes">
                {documentToRender.client_types.map(clientType => clientType.name).join(", ")}
            </td>
            <td className="date">
                <Moment format="DD/MM/YYYY HH:mm">
                    {documentToRender.created_at}
                </Moment>
            </td>
            <td className="date">{period}</td>
            <td className="status">{cancelStatus}</td>
            <td className="actions">
                <div className="btns-wrapper">
                    <Button variant="none" size="sm" className="btn-download" title="Télécharger la Pièce jointe" onClick={handleDownload}>
                        <span className="icon"></span>
                        <span className="text">Télécharger</span>
                    </Button>
                    {documentToRender.type === 'legal_cgv' && (
                        <Button variant="none" size="sm" className="btn-edit" title="Modifier la programmation" onClick={handleEdit}>
                            <span className="icon"></span>
                            <span className="text">Modifier</span>
                        </Button>
                    )}
                </div>
            </td>
        </tr>
    );
}

